import { AxiosResponse } from "axios";

import {
  Box,
  Button,
  Card,
  CardContent,
  Container,
  Grid2 as Grid,
  Skeleton,
  Typography,
} from "@mui/material";
import { useContext, useRef, useState } from "react";
import { Link as RouterLink } from "react-router-dom";
import { Link } from "@mui/material";
import logoWhite from "../assets/logo-white.png";
import { SessionContext } from "../providers/SessionProvider";
import { When } from "../components/logic/When";
import axios from "axios";
import { apiDownloadLatestGet } from "../api/download/latest/get";
import { apiOfflineActivationPost, type OfflineActivationResponse } from "../api/offline-activation/post";

const COLORS = {
  yellow: '#EBE32D',
  white: '#FFFFFF',
  grey: '#9AA0A6',
  lead: '#C9CDD1',
  line: 'rgba(255,255,255,.12)',
  cardBg: 'rgba(255,255,255,.035)',
};

const PLATFORM_DESCRIPTIONS: Record<string, string> = {
  CATIA: 'CATIA V5 plugin',
  Mastercam: 'Mastercam 2026 plugin',
  NX: 'Siemens NX plugin',
};

const getPlatformDescription = (name: string): string => {
  for (const [key, desc] of Object.entries(PLATFORM_DESCRIPTIONS)) {
    if (name.includes(key)) return desc;
  }
  return '';
};

const resolveInstallerFilename = (response: AxiosResponse, productId: string): string => {
  const contentDisposition = response.headers["content-disposition"];
  if (contentDisposition) {
    const filenameMatch = contentDisposition.match(/filename\*?=(?:UTF-8'')?([^;\r\n]+)/);
    if (filenameMatch) {
      const raw = decodeURIComponent(filenameMatch[1].trim());
      const basename = raw.split(/[\\/]/).pop() ?? raw;
      const safe = basename.replace(/[^A-Za-z0-9._-]/g, "_");
      if (safe && !safe.includes("..")) {
        return safe.endsWith(".msi") ? safe : `${safe}.msi`;
      }
    }
  }
  return `${productId}-Release-x64.msi`;
};

const handleDownloadResponse = (data: OfflineActivationResponse) => {
  const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = "DigitalCNC-Offline-Licence-Response.json";
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
};

export const ProductsPage = () => {
  const { products, productsLoading } = useContext(SessionContext);

  const [downloadingId, setDownloadingId] = useState<string | null>(null);

  const handleFetch = async (productId: string, platform: string = 'X64') => {
    setDownloadingId(productId);

    try {
      const response = await apiDownloadLatestGet(productId, platform);
      if (!response) {
        return;
      }
      const blob = response.data;
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = resolveInstallerFilename(response, productId);
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } finally {
      setDownloadingId(null);
    }
  };

  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      setSelectedFile(files[0]);
      setError(null);
    }
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleGenerateLicence = async () => {
    if (!selectedFile) {
      setError("Please select an offline request file.");
      return;
    }

    setIsGenerating(true);
    setError(null);

    try {
      const text = await selectedFile.text();
      let parsed: Record<string, unknown>;
      try {
        parsed = JSON.parse(text);
      } catch {
        setError("The selected file is not valid JSON. Please export a new request from DigitalCNC and try again.");
        setIsGenerating(false);
        return;
      }

      const formatVersion = parsed.formatVersion as string | undefined;
      const requestId = parsed.requestId as string | undefined;
      const productId = parsed.productId as unknown;
      const machineCode = parsed.machineCode as string | undefined;
      const generatedAtUtc = parsed.generatedAtUtc as string | undefined;

      if (formatVersion !== "1") {
        setError("Unsupported offline request format. Please export a new request from DigitalCNC.");
        setIsGenerating(false);
        return;
      }
      if (!requestId || typeof requestId !== "string" || requestId.trim().length === 0) {
        setError("The request file is missing a valid request ID. Please export a new request.");
        setIsGenerating(false);
        return;
      }
      if (typeof productId !== "number" || productId !== 30357) {
        setError("The request file has an invalid product ID. Please export a new request.");
        setIsGenerating(false);
        return;
      }
      if (!machineCode || typeof machineCode !== "string" || machineCode.trim().length === 0) {
        setError("The request file is missing a machine code. Please export a new request.");
        setIsGenerating(false);
        return;
      }
      if (!generatedAtUtc || typeof generatedAtUtc !== "string" || generatedAtUtc.trim().length === 0) {
        setError("The request file is missing a timestamp. Please export a new request.");
        setIsGenerating(false);
        return;
      }

      const response = await apiOfflineActivationPost({
        formatVersion: formatVersion,
        requestId: requestId,
        productId: productId as number,
        machineCode: machineCode,
        generatedAtUtc: generatedAtUtc,
      });

      if (!response?.data) {
        setError("The server did not return a response. Please check your connection and try again.");
        setIsGenerating(false);
        return;
      }

      const data = response.data;
      const responseFormatVersion = data.formatVersion as string | undefined;
      const responseRequestId = data.requestId as string | undefined;
      const responseProductId = data.productId as unknown;
      const serializedCertificate = data.serializedCertificate as string | undefined;
      const responseGeneratedAtUtc = data.generatedAtUtc as string | undefined;

      if (responseFormatVersion !== "1") {
        setError("The response has an unsupported format version. Please try again.");
        setIsGenerating(false);
        return;
      }
      if (!responseRequestId || typeof responseRequestId !== "string" || responseRequestId !== requestId) {
        setError("The response request ID does not match the uploaded request. Please try again.");
        setIsGenerating(false);
        return;
      }
      if (typeof responseProductId !== "number" || responseProductId !== 30357) {
        setError("The response has an invalid product ID. Please try again.");
        setIsGenerating(false);
        return;
      }
      if (!serializedCertificate || typeof serializedCertificate !== "string" || serializedCertificate.trim().length === 0) {
        setError("The licence response is missing the certificate. Please try again.");
        setIsGenerating(false);
        return;
      }
      if (!responseGeneratedAtUtc || typeof responseGeneratedAtUtc !== "string" || responseGeneratedAtUtc.trim().length === 0) {
        setError("The response is missing a timestamp. Please try again.");
        setIsGenerating(false);
        return;
      }

      handleDownloadResponse(data);
      setSelectedFile(null);
      setError(null);
    } catch (error) {
      if (axios.isAxiosError(error)) {
        const axiosError = error;
        if (axiosError.response) {
          const status = axiosError.response.status;
          if (status === 400) {
            const data = axiosError.response.data as { errors?: Record<string, string[]> };
            const validationErrors = data?.errors ? Object.values(data.errors).flat().join(" ") : "The request is invalid.";
            setError(`The offline request is invalid: ${validationErrors}`);
          } else if (status === 401) {
            setError("Your session has expired. Please sign in again.");
          } else if (status === 403) {
            setError("You are not authorised to generate this licence.");
          } else if (status === 409) {
            const data = axiosError.response.data as { title?: string };
            setError(data?.title || "This machine has reached its activation limit.");
          } else if (status >= 500) {
            setError("The server could not generate the offline licence.");
          } else {
            setError(`Server responded with HTTP ${status}.`);
          }
        } else if (axiosError.request) {
          setError("The server did not return a response. Please check your connection and try again.");
        } else {
          setError("A client error occurred. Please try again.");
        }
      } else if (error && typeof error === "object" && "errors" in error) {
        setError("The server did not return a response. Please check your connection and try again.");
      } else {
        setError("An unexpected error occurred. Please try again or contact support.");
      }
    } finally {
      setIsGenerating(false);
    }
  };

  return (
    <Box sx={{ minHeight: "100vh", bgcolor: COLORS.cardBg, color: COLORS.white }}>
      {/* Header */}
      <Box sx={{ borderBottom: `1px solid ${COLORS.line}`, py: 3, px: 4, bgcolor: 'rgba(0,0,0,0.2)' }}>
        <Container maxWidth="lg" sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box component="img" src={logoWhite} alt="DigitalCNC" sx={{ height: 36, display: 'block' }} />
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            <Box sx={{ fontSize: 14, fontWeight: 500, px: 2, py: 1, borderRadius: 8, backgroundColor: COLORS.yellow, color: '#1A1A1A' }}>
              Licences
            </Box>
            <Link component={RouterLink} to="/machines" sx={{ fontSize: 14, fontWeight: 500, textDecoration: 'none', px: 2, py: 1, borderRadius: 8, border: `1px solid rgba(255,255,255,.28)`, color: COLORS.white, transition: 'background .15s,color .15s,border-color .15s', '&:hover': { borderColor: COLORS.yellow, color: COLORS.yellow } }}>
              Machines
            </Link>
            <Link component={RouterLink} to="/login" sx={{ fontSize: 14, color: COLORS.grey, textDecoration: 'none', pl: 1, transition: 'color .15s', '&:hover': { color: COLORS.white } }}>
              Logout
            </Link>
          </Box>
        </Container>
      </Box>

      {/* Main Content */}
      <Container maxWidth="lg" sx={{ py: 6 }}>
        {/* Page Description */}
        <Box sx={{ mb: 6, maxWidth: 700 }}>
          <Typography variant="h4" sx={{ fontWeight: 600, mb: 2 }}>
            Your Licences
          </Typography>
          <Typography variant="body1" sx={{ color: COLORS.grey, lineHeight: 1.7 }}>
            Download the latest SDK versions and manage your licences.
          </Typography>
        </Box>

        {/* Products Grid */}
        <Grid container spacing={3}>
          {productsLoading ? (
            <>
              <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                <Skeleton variant="rectangular" height={200} sx={{ borderRadius: 2 }} />
              </Grid>
              <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                <Skeleton variant="rectangular" height={200} sx={{ borderRadius: 2 }} />
              </Grid>
              <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                <Skeleton variant="rectangular" height={200} sx={{ borderRadius: 2 }} />
              </Grid>
            </>
          ) : products.length === 0 ? (
            <Grid size={12}>
              <Typography sx={{ color: COLORS.grey, py: 4, textAlign: 'center' }}>
                No products found.
              </Typography>
            </Grid>
          ) : (
            products.map((product) => (
              <Grid size={{ xs: 12, sm: 6, md: 4 }} key={product.productId}>
                <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column', borderRadius: '14px', bgcolor: COLORS.cardBg, border: `1px solid ${COLORS.line}` }}>
                  <CardContent sx={{ p: 3, flexGrow: 1 }}>
                    <Typography variant="h6" sx={{ color: COLORS.white, fontWeight: 600, mb: 1 }}>
                      {product.name}
                    </Typography>
                    <Typography sx={{ color: COLORS.lead, fontSize: '14px', mb: 1 }}>
                      {getPlatformDescription(product.name)}
                    </Typography>
                    <Box sx={{ mt: 'auto' }}>
                      <When expr={product.isTrial && product.expired}>
                        <Typography sx={{ color: '#F44336', fontSize: '14px' }}>
                          Trial has expired. Upgrade for full access.
                        </Typography>
                      </When>
                      <When expr={!product.isTrial && product.expiresAt != null}>
                        <Typography sx={{ color: product.expired ? '#F44336' : COLORS.lead, fontSize: '14px' }}>
                          {product.expired
                            ? `Licence expired on ${new Date(product.expiresAt!).toLocaleDateString('en-GB')}.`
                            : `Licence expires on ${new Date(product.expiresAt!).toLocaleDateString('en-GB')}.`}
                        </Typography>
                      </When>
                      <Button
                        variant="contained"
                        size="small"
                        onClick={() => handleFetch(product.productId)}
                        disabled={product.isTrial || downloadingId === product.productId}
                        sx={{
                          mt: 2,
                        }}
                      >
                        {downloadingId === product.productId ? "Downloading..." : "Download Plugin"}
                      </Button>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            ))
          )}
        </Grid>

        {/* Offline Activation Card */}
        <Card sx={{ mt: 6, p: 4, borderRadius: '14px', bgcolor: COLORS.cardBg, border: `1px solid ${COLORS.line}` }}>
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
            Offline Activation
          </Typography>
          <Typography variant="body2" sx={{ color: COLORS.grey, mb: 3 }}>
            Activate DigitalCNC on a computer that cannot access the internet.
          </Typography>
          <Typography variant="body2" sx={{ color: COLORS.grey, mb: 3 }}>
            <ol style={{ margin: 0, paddingLeft: 20 }}>
              <li>Export an offline request from DigitalCNC.</li>
              <li>Upload the request here.</li>
              <li>Transfer the downloaded licence response back to the offline computer.</li>
            </ol>
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
            <Button
              variant="outlined"
              size="small"
              onClick={() => fileInputRef.current?.click()}
              sx={{
                borderRadius: '8px',
                fontSize: 13,
              }}
            >
              Choose Offline Request
            </Button>
            {selectedFile && (
              <Typography variant="body2" sx={{ color: COLORS.grey, fontSize: 13 }}>
                {selectedFile.name}
              </Typography>
            )}
            <input
              ref={fileInputRef}
              type="file"
              accept=".json"
              onChange={handleFileSelect}
              style={{ display: 'none' }}
              data-testid="offline-request-file-input"
            />
          </Box>
          <Button
            variant="contained"
            size="small"
            onClick={handleGenerateLicence}
            disabled={!selectedFile || isGenerating}
          >
            {isGenerating ? "Generating..." : "Generate Offline Licence"}
          </Button>
          {error && (
            <Typography role="alert" sx={{ mt: 2, color: '#F44336', fontSize: 14 }}>
              {error}
            </Typography>
          )}
        </Card>

        {/* Footer */}
        <Box sx={{ mt: 8, pt: 4, borderTop: `1px solid ${COLORS.line}`, textAlign: 'center' }}>
          <Box sx={{ mb: 2 }}>
            <img src={logoWhite} alt="DigitalCNC Logo" style={{ height: 28, opacity: 0.7 }} />
          </Box>
          <Typography variant="caption" sx={{ color: COLORS.grey, display: 'block', mb: 2 }}>
            <span>For security, your session will end automatically when you close this browser.</span>
          </Typography>
          <Typography variant="caption" sx={{ color: COLORS.grey, display: 'block', mb: 2 }}>
            <span>
              Need help? Contact{' '}
              <Link href="mailto:support@digitalcnc.ai" sx={{ color: COLORS.white, textDecoration: 'underline' }}>
                support@digitalcnc.ai
              </Link>
            </span>
          </Typography>
          <Typography variant="caption" sx={{ color: COLORS.grey, display: 'block' }}>
            <span>Copyright © 2026 DigitalCNC | Yorkshire AI Labs</span>
          </Typography>
          <Box sx={{ mt: 1, display: 'flex', justifyContent: 'center', gap: 2 }}>
            <Link href="mailto:support@digitalcnc.ai" sx={{ color: COLORS.grey, textDecoration: 'none', fontSize: 12, '&:hover': { color: COLORS.white } }}>
              Support
            </Link>
            <Link href="https://digitalcnc.ai/privacy-policy" sx={{ color: COLORS.grey, textDecoration: 'none', fontSize: 12, '&:hover': { color: COLORS.white } }}>
              Privacy Policy
            </Link>
            <Link href="https://digitalcnc.ai" sx={{ color: COLORS.grey, textDecoration: 'none', fontSize: 12, '&:hover': { color: COLORS.white } }}>
              digitalcnc.ai
            </Link>
          </Box>
        </Box>
      </Container>
    </Box>
  );
};